import { CoreMenu, CoreMenuGroup } from '@core/types';
import { Permission } from 'app/shared/models';

/**
 * Sidebar navigation, grouped into "square" cards.
 *
 * Each entry in `menuGroups` renders as its own bordered card in the sidebar.
 * The flat `menu` export below is kept for backward compatibility and is derived
 * from the groups, so callers that only need the flat list keep working.
 */
export const menuGroups: CoreMenuGroup[] = [
  // ── Data Insight (locked / coming soon) ────────────────────────────────────
  // Rendered blurred behind a lock overlay. Items have no real routes yet.
  // {
  //   id: 'data-insight',
  //   title: 'Data Insight',
  //   translate: 'DATAINSIGHT',
  //   icon: 'ri-bar-chart-box-line',
  //   locked: false,
  //   lockedLabel: 'Coming soon',
  //   children: [
  //     {
  //       id: 'data-insight-analytics',
  //       title: 'Analytics',
  //       translate: 'ANALYTICS',
  //       type: 'item',
  //       locked: false,
  //       icon: 'ri-line-chart-line',
  //       url: '/data-insight/analytics',
  //     },
  //     {
  //       id: 'data-insight-reports',
  //       title: 'Reports',
  //       translate: 'REPORTS',
  //       type: 'item',
  //       locked: false,
  //       icon: 'ri-file-chart-line',
  //       url: '/data-insight/reports',
  //     },
  //   ],
  // },

  // ── Core ────────────────────────────────────────────────────────────────
  // Dashboard, Partners, Products & Offers (+ Receipt / Consumer / Voucher /
  // Categories / Product Catalogue placed here for sensible grouping).
  {
    id: 'core',
    title: 'Core',
    translate: 'CORE',
    icon: 'ri-apps-2-line',
    children: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        translate: 'DASHBOARD',
        type: 'item',
        permissions: [Permission.DashboardAdmin],
        icon: 'ri-dashboard-line',
        url: '/dashboard/dashboard-admin',
      },
      {
        id: 'dashboard',
        title: 'Dashboard',
        translate: 'DASHBOARD',
        type: 'item',
        permissions: [Permission.DashboardProducer],
        icon: 'ri-dashboard-line',
        url: '/dashboard/dashboard-producer',
      },
      {
        id: 'partners',
        title: 'Partners',
        translate: 'PARTNERS',
        type: 'collapsible',
        // Include DashboardProducer so producer accounts can open the section and reach Staff (staff child also lists it).
        permissions: [Permission.PartnerAdmin, Permission.PartnerGroupAdmin, Permission.StoreAdmin, Permission.DashboardProducer],
        icon: 'ri-group-line',
        children: [
          {
            id: 'partner-group',
            title: 'Partner Group',
            translate: 'PARTNERGROUP',
            type: 'item',
            permissions: [Permission.PartnerGroupAdmin],
            icon: 'flaticon-menu-1',
            url: '/partners/partner-group',
          },
          {
            id: 'partner',
            title: 'Partner',
            translate: 'PARTNER',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-menu-1',
            url: '/partners/partner',
          },
          {
            id: 'brand',
            title: 'Brand',
            translate: 'BRAND',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-menu-1',
            url: '/partners/brand',
          },
          {
            id: 'staff',
            title: 'Staff',
            translate: 'STAFF',
            type: 'item',
            permissions: [Permission.PartnerAdmin, Permission.DashboardProducer, Permission.StoreAdmin],
            icon: 'ri-user-settings-line',
            url: '/partners/staff',
          },
          {
            id: 'branch',
            title: 'Branch',
            translate: 'BRANCH',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-menu-1',
            url: '/partners/branch',
          },
        ],
      },
      {
        id: 'products',
        title: 'Products',
        translate: 'PRODUCTS',
        type: 'item',
        permissions: [Permission.ProductAdmin],
        icon: 'ri-dice-line',
        url: '/products/product-list',
      },
      {
        id: 'offer',
        title: 'Offer',
        translate: 'OFFER',
        type: 'item',
        permissions: [Permission.OfferAdmin],
        icon: 'ri-price-tag-3-line',
        url: '/offer/offer-list',
      },
      {
        id: 'receipt',
        title: 'Receipt',
        translate: 'RECEIPT',
        type: 'item',
        permissions: [Permission.ReceiptAdmin],
        icon: 'ri-receipt-line',
        url: '/receipt/receipt-list',
      },
      {
        id: 'consumer',
        title: 'Consumer',
        translate: 'CONSUMER',
        type: 'item',
        permissions: [Permission.ConsumerAdmin],
        icon: 'ri-user-star-line',
        url: '/consumer/consumer-list',
      },
      {
        id: 'voucher',
        title: 'Voucher',
        translate: 'VOUCHER',
        type: 'item',
        permissions: [Permission.VoucherAdmin],
        icon: 'ri-shopping-cart-line',
        url: '/voucher/voucher-list',
      },
      {
        id: 'pending-delivery-list',
        title: 'Pending Deliveries',
        translate: 'PENDINGDELIVERIES',
        type: 'item',
        permissions: [Permission.DeliveryAdmin],
        icon: 'ri-book-read-line',
        url: 'delivery/pending-delivery-rewards-list',
      },
      {
        id: 'qr-code',
        title: 'QR Codes',
        translate: 'QRCODES',
        type: 'item',
        permissions: [Permission.PartnerAdmin],
        icon: 'ri-qr-code-line',
        url: '/qr-code/qr-code-list',
      },
      {
        id: 'ai-usage',
        title: 'AI Usage Monitoring',
        translate: 'AIUSAGE_MENU',
        type: 'item',
        permissions: [Permission.PartnerAdmin, Permission.DashboardAdmin],
        icon: 'ri-cpu-line',
        url: '/ai-usage/ai-usage-list',
      },
      {
        id: 'product-catalogue',
        title: 'Product Catalogue',
        translate: 'PRODUCTCATALOGUE',
        type: 'item',
        permissions: [Permission.SKUCatalogueAdmin],
        icon: 'ri-box-3-line',
        url: '/product-catalogue/product-catalogue-list',
      },
      {
        id: 'categories',
        title: 'Categories',
        translate: 'CATEGORIES',
        type: 'collapsible',
        permissions: [Permission.PartnerAdmin],
        icon: 'ri-store-2-line',
        children: [
          {
            id: 'category',
            title: 'Category',
            translate: 'CATEGORY',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'ri-store-2-line',
            url: '/category/category-list',
          },
          {
            id: 'subcategory1',
            title: 'Subcategory 1',
            translate: 'SUBCATEGORY1',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-comment',
            url: '/category/subcategory1-list',
          },
          {
            id: 'subcategory2',
            title: 'Subcategory 2',
            translate: 'SUBCATEGORY2',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-comment',
            url: '/category/subcategory2-list',
          },
          {
            id: 'subcategory3',
            title: 'Subcategory 3',
            translate: 'SUBCATEGORY3',
            type: 'item',
            permissions: [Permission.PartnerAdmin],
            icon: 'flaticon-comment',
            url: '/category/subcategory3-list',
          },
        ],
      },
    ],
  },

  // ── Marketing ─────────────────────────────────────────────────────────────
  // Banner, News, Spin & Targeted Marketing.
  {
    id: 'marketing',
    title: 'Marketing',
    translate: 'MARKETING',
    icon: 'ri-megaphone-line',
    children: [
      {
        id: 'banner',
        title: 'Banner',
        translate: 'BANNER',
        type: 'item',
        permissions: [Permission.BannerAdmin],
        icon: 'ri-advertisement-line',
        url: '/banner/banner-list',
      },
      {
        id: 'news',
        title: 'News',
        translate: 'NEWS',
        type: 'item',
        permissions: [Permission.NewsAdmin],
        icon: 'ri-newspaper-line',
        url: '/news/news-list',
      },
      {
        id: 'spin',
        title: 'Spin',
        translate: 'SPIN',
        type: 'item',
        permissions: [Permission.SpinAdmin],
        icon: 'ri-gift-line',
        url: '/spin/spin-question',
      },
      {
        id: 'targeted-marketing',
        title: 'Targeted Marketing',
        translate: 'TARGETEDMARKETING',
        type: 'item',
        permissions: [Permission.TargetedMarketingAdmin],
        icon: 'ri-focus-3-line',
        url: '/targeted-marketing/targeted-marketing-list',
      },
    ],
  },

  // ── Communication (Chat) ────────────────────────────────────────────────
  {
    id: 'communication',
    title: 'Communication',
    translate: 'COMMUNICATION',
    icon: 'ri-chat-2-line',
    children: [
      {
        id: 'chat',
        title: 'Chat',
        translate: 'CHAT',
        type: 'collapsible',
        permissions: [Permission.ChatConversationAdmin, Permission.ChatMessageAdmin],
        icon: 'ri-chat-2-line',
        children: [
          {
            id: 'conversation',
            title: 'Conversation',
            translate: 'CONVERSATION',
            type: 'item',
            permissions: [Permission.ChatConversationAdmin],
            icon: 'flaticon-chat',
            url: '/chat/conversation',
          },
          {
            id: 'reported-chat',
            title: 'Reported Chat',
            translate: 'REPORTEDCHAT',
            type: 'item',
            permissions: [Permission.ReportedChatAdmin],
            icon: 'flaticon-comment',
            url: '/chat/reported-chat',
          },
        ],
      },
    ],
  },

  // ── System ──────────────────────────────────────────────────────────────
  // Subscription, Audit Log, AI Editor & System Settings.
  {
    id: 'system',
    title: 'System',
    translate: 'SYSTEM',
    icon: 'ri-settings-3-line',
    children: [
      {
        id: 'subscription',
        title: 'Subscription',
        translate: 'SUBSCRIPTION',
        type: 'item',
        permissions: [Permission.PartnerAdmin],
        icon: 'ri-calendar-check-line',
        url: '/subscription/subscription-list',
      },
      {
        id: 'aieditor',
        title: 'AIEDITOR',
        translate: 'AIEDITOR',
        type: 'collapsible',
        permissions: [Permission.OpenAIPromptAdmin],
        icon: 'ri-robot-2-line',
        children: [
          {
            id: 'aieditor-openai',
            title: 'OPENAI',
            translate: 'OPENAI',
            type: 'item',
            permissions: [Permission.OpenAIPromptAdmin],
            icon: 'ri-terminal-box-line',
            url: '/aieditor/openai',
          },
          {
            id: 'aieditor-deepseek',
            title: 'DEEPSEEK',
            translate: 'DEEPSEEK',
            type: 'item',
            permissions: [Permission.OpenAIPromptAdmin],
            icon: 'ri-cpu-line',
            url: '/aieditor/deepseek',
          },
        ],
      },
      {
        id: 'audit-log',
        title: 'Audit Log',
        translate: 'AUDITLOG',
        type: 'item',
        permissions: [Permission.AuditLogAdmin],
        icon: 'ri-shield-check-line',
        url: '/audit-log/audit-log-list',
      },
      {
        id: 'system-settings',
        title: 'System Settings',
        translate: 'SYSTEMSETTINGS',
        type: 'item',
        permissions: [Permission.PartnerAdmin],
        icon: 'ri-settings-3-line',
        url: '/system-settings',
      },
    ],
  },

  // ── Rewards & Delivery (last) ─────────────────────────────────────────────
  {
    id: 'rewards-delivery',
    title: 'Rewards & Delivery',
    translate: 'REWARDSDELIVERY',
    icon: 'ri-gift-2-line',
    children: [
      {
        id: 'deliveryAdmin',
        title: 'Pending Deliveries',
        translate: 'PENDINGDELIVERIES',
        type: 'item',
        permissions: [Permission.DeliveryListAdmin],
        icon: 'ri-truck-line',
        url: '/delivery/delivery-list',
      },
      {
        id: 'reward_inventory',
        title: 'Rewards History',
        translate: 'REWARDINVENTORY',
        type: 'item',
        permissions: [Permission.OfferAdmin, Permission.UserRewardAdmin],
        icon: 'ri-gift-2-line',
        url: '/reward-inventory/reward-inventory-list',
      },
      {
        id: 'money_reward_inventory',
        title: 'Delivery Reward Balance',
        translate: 'MONEYREWARDINVENTORY',
        type: 'item',
        permissions: [Permission.OfferAdmin, Permission.UserRewardAdmin],
        icon: 'ri-money-dollar-circle-line',
        url: '/reward-inventory/money-reward-inventory',
      },

      {
        id: 'delivery-method',
        title: 'Delivery Methods',
        translate: 'DELIVERYMETHODS',
        type: 'item',
        permissions: [Permission.DeliveryMethodAdmin],
        icon: 'ri-route-line',
        url: '/delivery-method',
      },
    ],
  },
  {
    id: 'data-insight',
    title: 'Data Insight',
    translate: 'DATAINSIGHT',
    icon: 'ri-bar-chart-box-line',
    children: [
      {
        id: 'data-insight-page',
        title: 'Data Insight',
        translate: 'DATAINSIGHT',
        type: 'item',
        icon: 'ri-line-chart-line',
        url: '/data-insight',
      },
    ],
  },
  {
    id: 'help-center',
    title: 'Help Center',
    translate: 'HELPCENTER',
    icon: 'ri-question-line',
    children: [
      {
        id: 'help-center-page',
        title: 'Help Center',
        translate: 'HELPCENTER',
        type: 'item',
        icon: 'ri-question-line',
        url: '/help-center',
        exactMatch: false,
      },
    ],
  },
];

// Flattened list kept for backward compatibility with any consumer of `menu`.
export const menu: CoreMenu[] = menuGroups.reduce<CoreMenu[]>((acc, group) => acc.concat(group.children), []);
